/**
 * Sortable column headers (OPEN-204). Click a header to sort by it, click again to reverse; an
 * arrow shows the current sort and `aria-sort` tells screen readers. Pair with `useUrlSort` (the
 * sort lives in the URL) and, for a list held in full, `sortRows`.
 *
 * `SortableHead` is the shared shadcn `TableHead`; tables built from a plain `<th>` put
 * `SortButton` inside their own header cell and set `aria-sort` on it with `ariaSort`.
 */

import * as React from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { cn } from 'src/lib/utils';
import { ariaSort, type SortState } from '../../lib/url-state';
import { TableHead } from './table';

interface SortProps<K extends string> {
  /** The column this header sorts by. */
  readonly column: K;
  /** Null: no column sorted yet (the list keeps the order it arrived in). */
  readonly sort: SortState<K> | null;
  readonly onSort: (column: K) => void;
  readonly children: React.ReactNode;
}

/** The clickable label + arrow, for any header cell. */
export function SortButton<K extends string>({
  column,
  sort,
  onSort,
  children,
}: SortProps<K>): React.JSX.Element {
  const active = sort?.key === column;
  const Icon = !active ? ArrowUpDown : sort?.dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <button
      type="button"
      onClick={(): void => onSort(column)}
      className={cn(
        'inline-flex items-center gap-1 font-medium hover:text-foreground',
        active && 'text-foreground',
      )}
      style={{
        background: 'transparent',
        border: 'none',
        padding: 0,
        color: 'inherit',
        font: 'inherit',
        cursor: 'pointer',
      }}
    >
      {children}
      <Icon
        aria-hidden
        size={14}
        className={cn('shrink-0', !active && 'opacity-40')}
        data-sort={active ? sort?.dir : 'none'}
      />
    </button>
  );
}

/** A shared-`Table` header cell that sorts its column. */
export function SortableHead<K extends string>({
  className,
  ...props
}: SortProps<K> & { readonly className?: string }): React.JSX.Element {
  return (
    <TableHead aria-sort={ariaSort(props.sort, props.column)} className={className}>
      <SortButton {...props} />
    </TableHead>
  );
}
